import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, Phone, RefreshCw, Package, Truck, Printer,
  Save, Trash2, Search, Eye, Pencil, MapPin, Box,
  Loader2, CheckCircle2, X, ChevronUp, ChevronDown, Globe, Tag, Calendar, User,
} from 'lucide-react';
import { adminAPI, websiteSettingsAPI } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import './Admin.css';
import './OrderManagement.css';
import '../../components/admin/MobileCardList.css';
import {
  OrderStatusBadge,
  TrafficSourceBadge,
  CustomerIntelligenceWidget,
  ViewOrderModal,
  BulkSendResultModal,
  PickupResultModal,
  ManualOrderModal,
  EditOrderModal,
  CustomerRiskBadge,
  CustomerRiskSummary,
  normalizeCustomerPhone,
} from '../../components/admin/orders';
import {
  exportMohammadParcelXLSX,
  exportProductDealerXLSX,
  exportStandardXLSX,
  exportStandardCSV,
} from '../../utils/orderExport';
import { getImageUrl } from '../../utils/imageUrl';

const STATUS_OPTIONS = ['preorder', 'pending', 'no_response', 'processing', 'shipped', 'in_querier', 'partially_delivered', 'delivered', 'cancelled'] as const;
type StatusFilter = 'all' | 'new' | 'incomplete' | 'future' | 'not_response' | 'confirmed' | 'confirmed_sent' | 'in_querier' | 'partially_delivered' | 'delivered' | 'cancelled'
                  | 'preorder' | 'pending' | 'no_response' | 'processing' | 'shipped';

// Which individual statuses belong to each group
const STATUS_GROUPS: Record<string, string[]> = {
  new:                 ['pending'],
  incomplete:          ['preorder'],
  future:              ['preorder'],
  not_response:        ['no_response'],
  confirmed:           ['processing'],
  in_querier:          ['shipped', 'in_querier'],
  partially_delivered: ['partially_delivered'],
  delivered:           ['delivered'],
  cancelled:           ['cancelled'],
};

const cleanUnicode = (str: any): string => {
  return String(str || '')
    .normalize('NFC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
};

const convertBanglaToEnglishDigits = (str: string): string => {
  const banglaDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return String(str || '').replace(/[০-৯]/g, (d) => String(banglaDigits.indexOf(d)));
};

const BANGLA_CHAR_MAP: Record<string, string> = {
  'অ': 'o', 'আ': 'a', 'ই': 'i', 'ঈ': 'i', 'উ': 'u', 'ঊ': 'u', 'ঋ': 'ri', 'এ': 'e', 'ঐ': 'oi', 'ও': 'o', 'ঔ': 'ou',
  'া': 'a', 'ি': 'i', 'ী': 'i', 'ু': 'u', 'ূ': 'u', 'ৃ': 'ri', 'ে': 'e', 'ৈ': 'oi', 'ো': 'o', 'ৌ': 'ou',
  'ক': 'k', 'খ': 'kh', 'গ': 'g', 'ঘ': 'gh', 'ঙ': 'ng',
  'চ': 'ch', 'ছ': 'ch', 'জ': 'j', 'ঝ': 'jh', 'ঞ': 'n',
  'ট': 't', 'ঠ': 'th', 'ড': 'd', 'ঢ': 'dh', 'ণ': 'n',
  'ত': 't', 'থ': 'th', 'দ': 'd', 'ধ': 'dh', 'ন': 'n',
  'প': 'p', 'ফ': 'f', 'ব': 'b', 'ভ': 'v', 'ম': 'm',
  'য': 'j', 'র': 'r', 'ল': 'l', 'শ': 'sh', 'ষ': 'sh', 'স': 's', 'হ': 'h',
  'ড়': 'r', 'ঢ়': 'rh', 'য়': 'y', 'ৎ': 't', 'ং': 'ng', 'ঃ': 'h', 'ঁ': ''
};

const banglaToLatin = (str: string): string => {
  let res = '';
  for (const ch of str) {
    if (ch === '্' || ch === '\u200D' || ch === '\u200C') continue;
    res += BANGLA_CHAR_MAP[ch] !== undefined ? BANGLA_CHAR_MAP[ch] : ch;
  }
  return res.toLowerCase();
};

const COMMON_BANGLA_MAP: Record<string, string[]> = {
  // Cities / Locations
  'ঢাকা': ['dhaka'],
  'মিরপুর': ['mirpur'],
  'উত্তরা': ['uttara'],
  'ধানমন্ডি': ['dhanmondi'],
  'বনানী': ['banani'],
  'গুলশান': ['gulshan'],
  'মোহাম্মদপুর': ['mohammadpur', 'mohammedpur'],
  'বাড্ডা': ['badda'],
  'যাত্রাবাড়ী': ['jatrabari'],
  'খিলগাঁও': ['khilgaon'],
  'মতিঝিল': ['motijheel', 'motijhil'],
  'ফার্মগেট': ['farmgate'],
  'তেজগাঁও': ['tejgaon'],
  'বাসাবো': ['basabo'],
  'মালিবাগ': ['malibagh', 'malibag'],
  'মগবাজার': ['mogbazar', 'moghbazar'],
  'লালবাগ': ['lalbagh', 'lalbag'],
  'ওয়ারী': ['wari'],
  'কেরানীগঞ্জ': ['keraniganj'],
  'সাভার': ['savar'],
  'গাজীপুর': ['gazipur'],
  'নারায়ণগঞ্জ': ['narayanganj'],
  'চট্টগ্রাম': ['chittagong', 'chattogram'],
  'সিলেট': ['sylhet'],
  'রাজশাহী': ['rajshahi'],
  'খুলনা': ['khulna'],
  'বরিশাল': ['barisal', 'barishal'],
  'রংপুর': ['rangpur'],
  'ময়মনসিংহ': ['mymensingh'],
  'কুমিল্লা': ['comilla', 'cumilla'],
  'বগুড়া': ['bogra', 'bogura'],
  'ফেনী': ['feni'],
  'নোয়াখালী': ['noakhali'],
  'যশোর': ['jessore', 'jashore'],
  'কক্সবাজার': ['cox', "cox's bazar"],
  // Common Customer Names
  'উজ্জল': ['uzzal', 'ujjal'],
  'পাভেল': ['pavel'],
  'শুভ': ['shuvo', 'subho'],
  'শোভন': ['shovon', 'shoyon'],
  'আহমেদ': ['ahmed'],
  'হাসান': ['hasan', 'hassan'],
  'হোসেন': ['hossain', 'hussain'],
  'খান': ['khan'],
  'আলী': ['ali'],
  'রহমান': ['rahman'],
  'করিম': ['karim'],
  'রহিম': ['rahim'],
  'নাসরিন': ['nasrin'],
  'নুসরাত': ['nusrat'],
  'সাদিয়া': ['sadia'],
  'তানভীর': ['tanvir'],
  'আরিফ': ['arif', 'ariful'],
  'রাশেদ': ['rashed'],
  'সোহেল': ['sohel'],
  'রুবেল': ['rubel'],
  'রানা': ['rana'],
  'মিয়া': ['miah', 'mia'],
  'মোহাম্মদ': ['mohammad', 'md'],
  'মেহেদী': ['mehedi'],
  // Common Product Categories / Types
  'জিন্স': ['jeans', 'denim'],
  'ডেনিম': ['denim', 'jeans'],
  'শার্ট': ['shirt'],
  'টি-শার্ট': ['t-shirt', 'tshirt'],
  'টিশার্ট': ['t-shirt', 'tshirt'],
  'শাড়ি': ['sharee', 'saree'],
  'শাড়ি': ['sharee', 'saree'],
  'সিল্ক': ['silk'],
  'পাঞ্জাবি': ['panjabi', 'punjabi'],
  'বোরকা': ['borka', 'burqa'],
  'হিজাব': ['hijab'],
  'ঘড়ি': ['watch'],
  'ঘড়ি': ['watch'],
  'জুতো': ['shoes', 'shoe'],
  'জুতা': ['shoes', 'shoe'],
  'ব্যাগ': ['bag'],
};

const getSearchVariants = (search: string): string[] => {
  const clean = cleanUnicode(search).toLowerCase();
  if (!clean) return [];
  const normalizedDigits = convertBanglaToEnglishDigits(clean);
  const variants = new Set<string>([clean, normalizedDigits]);

  for (const [bn, enList] of Object.entries(COMMON_BANGLA_MAP)) {
    if (clean.includes(bn)) {
      enList.forEach(en => {
        variants.add(clean.replace(bn, en));
        variants.add(en);
      });
    }
    for (const en of enList) {
      if (clean.includes(en)) {
        variants.add(clean.replace(en, bn));
        variants.add(bn);
      }
    }
  }

  // Also add phonetic Latin transliteration if search contains Bangla characters
  if (/[\u0980-\u09FF]/.test(clean)) {
    const latin = banglaToLatin(clean);
    if (latin) variants.add(latin);
  }

  return Array.from(variants);
};

const matchesPhone = (orderPhone: string | undefined | null, search: string): boolean => {
  if (!orderPhone || !search) return false;
  const rawOrder = convertBanglaToEnglishDigits(cleanUnicode(orderPhone)).trim().toLowerCase();
  const rawSearch = convertBanglaToEnglishDigits(cleanUnicode(search)).trim().toLowerCase();
  if (rawOrder.includes(rawSearch)) return true;

  const orderDigits = rawOrder.replace(/\D/g, '');
  const searchDigits = rawSearch.replace(/\D/g, '');

  if (searchDigits.length >= 3) {
    if (orderDigits.includes(searchDigits)) return true;
    if (searchDigits.includes(orderDigits) && orderDigits.length >= 7) return true;

    // Normalize BD country codes (880 or 88)
    const normOrder = orderDigits.replace(/^(880|88)/, '0');
    const normSearch = searchDigits.replace(/^(880|88)/, '0');

    if (normOrder.includes(normSearch)) return true;

    // Compare without leading zeros (e.g. searching 1711223344 for 01711223344)
    const orderNoZero = normOrder.replace(/^0+/, '');
    const searchNoZero = normSearch.replace(/^0+/, '');

    if (orderNoZero.includes(searchNoZero)) return true;
  }
  return false;
};

const matchesOrderSearch = (order: any, search: string): boolean => {
  if (!search) return true;
  const rawSearch = cleanUnicode(search);
  if (!rawSearch) return true;

  // 1. Customer phone (smart normalization)
  if (matchesPhone(order.customer_phone, rawSearch)) return true;

  const variants = getSearchVariants(rawSearch);

  // Build searchable text corpus for order with unicode normalization
  const orderNumber = cleanUnicode(order.order_number).toLowerCase();
  const customerName = cleanUnicode(order.customer_name).toLowerCase();
  const shippingAddress = cleanUnicode(order.shipping_address).toLowerCase();
  const notes = cleanUnicode(order.notes).toLowerCase();
  const trackingCode = cleanUnicode(order.steadfast_tracking_code).toLowerCase();
  const consignmentId = cleanUnicode(order.steadfast_consignment_id).toLowerCase();

  // Product items (both English and Bengali names)
  const itemsText = Array.isArray(order.items)
    ? order.items.map((it: any) =>
        `${cleanUnicode(it.product_name)} ${cleanUnicode(it.product_name_bn)} ${cleanUnicode(it.name)} ${cleanUnicode(it.name_bn)}`
      ).join(' ').toLowerCase()
    : '';

  for (const variant of variants) {
    const v = variant.toLowerCase().trim();
    if (!v) continue;
    if (orderNumber && orderNumber.includes(v)) return true;
    if (customerName && customerName.includes(v)) return true;
    if (shippingAddress && shippingAddress.includes(v)) return true;
    if (notes && notes.includes(v)) return true;
    if (trackingCode && trackingCode.includes(v)) return true;
    if (consignmentId && consignmentId.includes(v)) return true;
    if (itemsText && itemsText.includes(v)) return true;
  }

  return false;
};

const STATUS_FILTER_LABELS: Record<string, string> = {
  all: 'All',
  new: 'New Orders',
  confirmed: 'Confirmed',
  in_querier: 'In Querier',
  confirmed_sent: 'Ready To Delivery',
  partially_delivered: 'Partially Delivered',
  delivered: 'Delivered',
  not_response: 'Not Response',
  cancelled: 'Cancelled',
  incomplete: 'Incomplete',
};

const renderHighlightedPhone = (phone: string | undefined | null, search: string): React.ReactNode => {
  if (!phone) return '—';
  if (!search || !search.trim()) return phone;

  const rawPhone = String(phone);
  const normalizedSearch = convertBanglaToEnglishDigits(search).trim();
  const searchDigits = normalizedSearch.replace(/\D/g, '');

  // 1. Direct case-insensitive literal substring match
  const lowerPhone = rawPhone.toLowerCase();
  const lowerSearch = normalizedSearch.toLowerCase();
  const directIdx = lowerPhone.indexOf(lowerSearch);
  if (directIdx !== -1 && lowerSearch.length > 0) {
    const before = rawPhone.slice(0, directIdx);
    const match = rawPhone.slice(directIdx, directIdx + lowerSearch.length);
    const after = rawPhone.slice(directIdx + lowerSearch.length);
    return (
      <>
        {before}
        <mark className="om-phone-highlight">{match}</mark>
        {after}
      </>
    );
  }

  // 2. If search has digits, check for digit-level match in phone
  if (searchDigits.length >= 3) {
    const digitIdx = rawPhone.indexOf(searchDigits);
    if (digitIdx !== -1) {
      const before = rawPhone.slice(0, digitIdx);
      const match = rawPhone.slice(digitIdx, digitIdx + searchDigits.length);
      const after = rawPhone.slice(digitIdx + searchDigits.length);
      return (
        <>
          {before}
          <mark className="om-phone-highlight">{match}</mark>
          {after}
        </>
      );
    }

    const searchNo88 = searchDigits.replace(/^(880|88)/, '0');
    if (searchNo88.length >= 3 && rawPhone.indexOf(searchNo88) !== -1) {
      const idx = rawPhone.indexOf(searchNo88);
      const before = rawPhone.slice(0, idx);
      const match = rawPhone.slice(idx, idx + searchNo88.length);
      const after = rawPhone.slice(idx + searchNo88.length);
      return (
        <>
          {before}
          <mark className="om-phone-highlight">{match}</mark>
          {after}
        </>
      );
    }

    const searchNoZero = searchDigits.replace(/^(880|88|0)/, '');
    if (searchNoZero.length >= 3 && rawPhone.indexOf(searchNoZero) !== -1) {
      const idx = rawPhone.indexOf(searchNoZero);
      const before = rawPhone.slice(0, idx);
      const match = rawPhone.slice(idx, idx + searchNoZero.length);
      const after = rawPhone.slice(idx + searchNoZero.length);
      return (
        <>
          {before}
          <mark className="om-phone-highlight">{match}</mark>
          {after}
        </>
      );
    }

    const phoneDigitsMap: number[] = [];
    let cleanPhoneDigits = '';
    for (let i = 0; i < rawPhone.length; i++) {
      if (/\d/.test(rawPhone[i])) {
        cleanPhoneDigits += rawPhone[i];
        phoneDigitsMap.push(i);
      }
    }

    const matchTargets = [searchDigits, searchNo88, searchNoZero].filter(s => s && s.length >= 3);
    for (const target of matchTargets) {
      const dIdx = cleanPhoneDigits.indexOf(target);
      if (dIdx !== -1 && dIdx + target.length <= phoneDigitsMap.length) {
        const startCharIdx = phoneDigitsMap[dIdx];
        const endCharIdx = phoneDigitsMap[dIdx + target.length - 1] + 1;
        const before = rawPhone.slice(0, startCharIdx);
        const match = rawPhone.slice(startCharIdx, endCharIdx);
        const after = rawPhone.slice(endCharIdx);
        return (
          <>
            {before}
            <mark className="om-phone-highlight">{match}</mark>
            {after}
          </>
        );
      }
    }
  }

  return rawPhone;
};

const renderHighlightedText = (text: string | undefined | null, search: string): React.ReactNode => {
  if (!text) return '—';
  if (!search || !search.trim()) return text;
  const rawText = String(text);
  const lowerText = rawText.toLowerCase();
  const lowerSearch = search.trim().toLowerCase();
  const idx = lowerText.indexOf(lowerSearch);
  if (idx === -1) return rawText;
  const before = rawText.slice(0, idx);
  const match = rawText.slice(idx, idx + lowerSearch.length);
  const after = rawText.slice(idx + lowerSearch.length);
  return (
    <>
      {before}
      <mark className="om-text-highlight">{match}</mark>
      {after}
    </>
  );
};

const format12HourDateTime = (dateStr: string | undefined | null, includeYear = false): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  const year = includeYear ? ` ${d.getFullYear()}` : '';
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${day}${year}, ${time}`;
};

const OrderManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [trafficSourceFilter, setTrafficSourceFilter] = useState<string>('all');
  const [sendingToSteadfast, setSendingToSteadfast] = useState<number | null>(null);

  // Customer courier fraud intelligence cache for visible rows
  const [customerRiskMap, setCustomerRiskMap] = useState<Record<string, CustomerRiskSummary>>({});
  const [riskLoadingPhones, setRiskLoadingPhones] = useState<Set<string>>(new Set());

  // Bulk Send to Steadfast (create consignments)
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSendResult, setBulkSendResult] = useState<{ sent: number; failed: number; already_sent: number; results: any[] } | null>(null);

  // Pickup Request (physical pickup / manifest)
  const [pickupRequesting, setPickupRequesting] = useState(false);
  const [pickupResult, setPickupResult] = useState<{ requested: number; already_requested: number; total_cod: number; pickup_time: string; results: any[]; pickup_api_success?: boolean; pickup_api_error?: string; pickup_id?: number } | null>(null);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Bulk Category Assignment
  const [assignCategory, setAssignCategory] = useState<string>('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Per-row inline category update
  const [categoryUpdating, setCategoryUpdating] = useState<Set<number>>(new Set());

  // Print Labels
  const [printingLabel, setPrintingLabel] = useState<number | null>(null);
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [labelFormat, setLabelFormat] = useState<string>(() => localStorage.getItem('order_label_format') || '4x3');

  // Steadfast Status Sync
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>('');
  const [syncStats, setSyncStats] = useState<{ synced: number; changed: number } | null>(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  // Edit modal & Manual modal state
  const [deliveryOptions, setDeliveryOptions] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [siteSettings, setSiteSettings] = useState<{ site_name: string; support_phone: string; logo_url?: string }>({ site_name: '', support_phone: '' });

  // Export Dropdown State
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const filterWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = filterWrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0 && el.scrollWidth > el.clientWidth) {
        e.preventDefault();
        el.scrollBy({ left: e.deltaY });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setExportDropdownOpen(false);
      }
    };
    if (exportDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportDropdownOpen]);

  // Date range filter
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateFilterBasis, setDateFilterBasis] = useState<'auto' | 'status_date' | 'created_at'>('auto');

  // Helper to resolve the relevant date of an order based on current filter & date basis
  const getOrderFilterDate = (order: any, filter: StatusFilter, basis: 'auto' | 'created_at' | 'status_date'): Date | null => {
    if (!order) return null;
    if (basis === 'created_at') {
      return order.created_at ? new Date(order.created_at) : null;
    }
    if (basis === 'status_date') {
      const d = order.status_changed_at || order.confirmed_at || order.shipped_at || order.delivered_at || order.created_at;
      return d ? new Date(d) : null;
    }
    // 'auto' mode: Smart contextual date according to the active category/status filter
    if (filter === 'confirmed') {
      const d = order.confirmed_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'in_querier' || filter === 'confirmed_sent') {
      const d = order.shipped_at || order.steadfast_sent_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'delivered') {
      const d = order.delivered_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'cancelled') {
      const d = order.cancelled_at || order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    if (filter === 'partially_delivered') {
      const d = order.status_changed_at || order.created_at;
      return d ? new Date(d) : null;
    }
    return order.created_at ? new Date(order.created_at) : null;
  };

  // Helper to resolve milestone timestamps for an order across all lifecycle stages
  const getOrderMilestones = (order: any) => {
    const currentCategory = getOrderCategory(order);
    const confirmedTime = order.confirmed_at || (currentCategory === 'confirmed' ? (order.status_changed_at || order.updated_at) : null);
    const courierTime = order.shipped_at || order.steadfast_sent_at || (['in_querier', 'confirmed_sent'].includes(currentCategory) ? (order.status_changed_at || order.updated_at) : null);
    const deliveredTime = order.delivered_at || (currentCategory === 'delivered' ? (order.status_changed_at || order.updated_at) : null);
    const cancelledTime = order.cancelled_at || (currentCategory === 'cancelled' ? (order.status_changed_at || order.updated_at) : null);
    const partialTime = (currentCategory === 'partially_delivered' && !deliveredTime) ? (order.status_changed_at || order.updated_at) : null;
    const noResponseTime = (currentCategory === 'not_response') ? (order.status_changed_at || order.updated_at) : null;
    const incompleteTime = (currentCategory === 'incomplete') ? (order.status_changed_at || order.updated_at) : null;
    const hasMilestones = !!(confirmedTime || courierTime || deliveredTime || cancelledTime || partialTime || noResponseTime || incompleteTime);

    return {
      currentCategory,
      confirmedTime,
      courierTime,
      deliveredTime,
      cancelledTime,
      partialTime,
      noResponseTime,
      incompleteTime,
      hasMilestones
    };
  };

  // Table: sort & pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Manual Order state
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const handleOpenManualModal = () => setManualModalOpen(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadOrders();
    loadDeliveryOptions();
    websiteSettingsAPI.getSettings().then(s => {
      setSiteSettings({ site_name: s.site_name || '', support_phone: s.support_phone || '', logo_url: s.logo_url || undefined });
    }).catch(() => {});
  }, []);

  const loadDeliveryOptions = async () => {
    try {
      const res = await adminAPI.getDeliveryOptions();
      const data = res.data || res;
      const list = Array.isArray(data) ? data : (data.results || []);
      setDeliveryOptions(list);
    } catch (err) {
      console.error('Failed to load delivery options:', err);
    }
  };

  // Listen for refresh event from AdminLayout header
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleRefresh = () => {
      loadOrders();
    };
    window.addEventListener('admin-refresh', handleRefresh);
    return () => window.removeEventListener('admin-refresh', handleRefresh);
  }, []);

  // Auto-reload editingOrder with fresh data when orders list changes
  useEffect(() => {
    if (editingOrder && editModalOpen) {
      const updatedOrder = orders.find(o => o.id === editingOrder.id);
      if (updatedOrder && updatedOrder !== editingOrder) {
        setEditingOrder(updatedOrder);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, editModalOpen]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getOrders();
      const data = response.data || response;
      const ordersList = Array.isArray(data) ? data : (data.results || []);
      console.log('Orders loaded:', ordersList.length, 'First order:', ordersList[0]);
      setOrders(ordersList);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToSteadfast = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to send this order to Steadfast Courier?')) {
      return;
    }

    setSendingToSteadfast(orderId);
    try {
      const response = await adminAPI.sendOrderToSteadfast(orderId);
      const data = response.data;
      
      if (data.success) {
        alert(`✅ Order sent to Steadfast successfully!\n\nConsignment ID: ${data.consignment_id}\nTracking Code: ${data.tracking_code}`);
        // Reload orders to get updated data
        await loadOrders();
      } else {
        alert(`Failed to send order to Steadfast: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Failed to send order to Steadfast:', error?.response?.status, error?.response?.data || error?.message || error);
      
      // Extract detailed error from response
      const responseData = error.response?.data;
      let errorMessage = 'Failed to send order to Steadfast.';
      
      if (responseData) {
        // Handle case where server returns plain text/HTML instead of JSON
        if (typeof responseData === 'string') {
          // Strip HTML tags if it's an HTML error page
          const cleanText = responseData.replace(/<[^>]*>/g, '').trim().substring(0, 300);
          errorMessage = cleanText || 'Server returned an unexpected error. Check server logs.';
        } else {
          errorMessage = responseData.error || responseData.detail || errorMessage;
        
          // Show validation errors if present
          if (responseData.validation_errors && responseData.validation_errors.length > 0) {
            errorMessage += '\n\nValidation Issues:\n• ' + responseData.validation_errors.join('\n• ');
          }
          
          // Show environment info for debugging
          if (responseData.environment) {
            errorMessage += `\n\nEnvironment: ${responseData.environment}`;
          }
          
          // Show API details if available
          if (responseData.details) {
            const details = responseData.details;
            const apiMessage = details.message || details.raw_response;
            if (apiMessage) {
              errorMessage += `\n\nAPI Message: ${apiMessage}`;
            }
            if (details.errors && typeof details.errors === 'object') {
              const fieldErrors = Object.entries(details.errors)
                .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                .join('\n• ');
              if (fieldErrors) {
                errorMessage += `\n\nField Errors:\n• ${fieldErrors}`;
              }
            }
          }
        }
      } else {
        errorMessage += '\n\n' + (error.message || 'Network error. Please check your connection.');
      }
      
      alert(`❌ ${errorMessage}`);
    } finally {
      setSendingToSteadfast(null);
    }
  };

  // ── Bulk Send to Steadfast (create consignments) ──
  const handleBulkSendToSteadfast = async () => {
    // Selected orders that do NOT have consignment IDs yet (any status)
    const eligible = selectedRows.filter(o => !o.steadfast_consignment_id);
    if (eligible.length === 0) return;

    setBulkSending(true);
    try {
      const orderIds = eligible.map((o: any) => o.id);
      const response = await adminAPI.bulkSendToSteadfast(orderIds);
      const data = response.data || {};

      let results: any[] = Array.isArray(data.results) ? [...data.results] : [];
      if (results.length === 0 && (data.sent_orders || data.failed_orders || data.skipped_orders)) {
        const sentList = (data.sent_orders || []).map((s: any) => ({
          order: s.order_number || s.id,
          consignment_id: s.consignment_id,
          tracking_code: s.tracking_code,
          success: true,
          message: 'Sent to Steadfast',
        }));
        const failedList = (data.failed_orders || []).map((f: any) => ({
          order: f.order_number || f.id,
          success: false,
          message: f.error || 'Failed to send',
        }));
        const skippedList = (data.skipped_orders || []).map((sk: any) => ({
          order: sk.order_number || sk.id,
          consignment_id: sk.consignment_id,
          tracking_code: sk.tracking_code,
          already_sent: true,
          message: sk.reason || 'Already sent',
        }));
        results = [...sentList, ...failedList, ...skippedList];
      }

      if (results.length === 0 && eligible.length > 0) {
        results = eligible.map((o: any) => ({
          order: o.order_number,
          success: data.success !== false,
          consignment_id: o.steadfast_consignment_id,
          tracking_code: o.steadfast_tracking_code,
          message: data.message || (data.success !== false ? 'Sent to Steadfast' : 'Failed to send'),
        }));
      }

      const sentCount = typeof data.sent === 'number'
        ? data.sent
        : (Array.isArray(data.sent_orders) ? data.sent_orders.length : results.filter((r: any) => r.success && !r.already_sent).length);

      const failedCount = typeof data.failed === 'number'
        ? data.failed
        : (Array.isArray(data.failed_orders) ? data.failed_orders.length : results.filter((r: any) => !r.success).length);

      const alreadySentCount = typeof data.already_sent === 'number'
        ? data.already_sent
        : (Array.isArray(data.skipped_orders) ? data.skipped_orders.length : results.filter((r: any) => r.already_sent).length);

      setBulkSendResult({
        sent: sentCount,
        failed: failedCount,
        already_sent: alreadySentCount,
        results,
      });
      await loadOrders();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
      setBulkSendResult({
        sent: 0,
        failed: eligible.length,
        already_sent: 0,
        results: eligible.map((o: any) => ({ order: o.order_number, success: false, message: msg })),
      });
    } finally {
      setBulkSending(false);
    }
  };

  // ── Pickup Request (physical pickup / manifest) ──
  const handleBulkPickupRequest = async () => {
    // Selected orders that HAVE consignment IDs (already sent to Steadfast)
    const eligible = selectedRows.filter(o => !!o.steadfast_consignment_id);
    if (eligible.length === 0) return;

    setPickupRequesting(true);
    try {
      const orderIds = eligible.map((o: any) => o.id);
      const response = await adminAPI.bulkPickupRequest(orderIds);
      const data = response.data || {};

      let results: any[] = Array.isArray(data.results) ? [...data.results] : [];
      if (results.length === 0 && eligible.length > 0) {
        results = eligible.map((o: any) => ({
          order: o.order_number,
          consignment_id: o.steadfast_consignment_id,
          tracking_code: o.steadfast_tracking_code,
          customer_name: o.customer_name,
          cod_amount: parseFloat(o.final_amount) || 0,
          already_requested: false,
          pickup_failed: data.success === false,
          message: data.message || (data.success !== false ? 'Pickup requested' : 'Pickup failed'),
        }));
      }

      setPickupResult({
        requested: typeof data.requested === 'number' ? data.requested : (data.success !== false ? eligible.length : 0),
        already_requested: data.already_requested ?? 0,
        total_cod: typeof data.total_cod === 'number' ? data.total_cod : eligible.reduce((acc: number, curr: any) => acc + (parseFloat(curr.final_amount) || 0), 0),
        pickup_time: data.pickup_time || data.pickup_requested_at || '',
        results,
        pickup_api_success: data.pickup_api_success ?? data.success,
        pickup_api_error: data.pickup_api_error || (data.success === false ? data.message : undefined),
        pickup_id: data.pickup_id,
      });
      await loadOrders();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
      setPickupResult({
        requested: 0,
        already_requested: 0,
        total_cod: 0,
        pickup_time: '',
        results: eligible.map((o: any) => ({ order: o.order_number, consignment_id: o.steadfast_consignment_id, already_requested: false, message: msg })),
      });
    } finally {
      setPickupRequesting(false);
    }
  };

  const handleEditOrder = (order: any) => {
    setEditingOrder(order);
    setEditModalOpen(true);
  };

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteOrder = async (orderId: number) => {
    const targetOrder = orders.find(o => o.id === orderId);
    const identifier = targetOrder ? `#${targetOrder.order_number}` : `ID ${orderId}`;
    if (!window.confirm(`Are you sure you want to permanently delete order ${identifier}?\n\nThis action cannot be undone and will automatically restore product stock.`)) {
      return;
    }

    try {
      await adminAPI.deleteOrder(orderId);
      if (selectedOrder && selectedOrder.id === orderId) {
        closeViewModal();
      }
      setSelectedRows(prev => prev.filter(id => id !== orderId));
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to delete order:', error);
      alert(`Error: ${error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete order'}`);
    }
  };

  // ── Print Single Label ──
  const handlePrintSingleLabel = async (orderId: number, size?: string) => {
    setPrintingLabel(orderId);
    try {
      const chosenSize = size || labelFormat || '4x3';
      const res = await adminAPI.generateLabels([orderId], undefined, chosenSize);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      if (printWin) {
        printWin.addEventListener('load', () => {
          setTimeout(() => { try { printWin.print(); } catch {} }, 600);
        });
      }
    } catch (error: any) {
      console.error('Failed to print label:', error);
      alert(`Failed to generate label: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    } finally {
      setPrintingLabel(null);
    }
  };

  // ── Bulk Print Labels ──
  const handleBulkPrintLabels = async (size?: string) => {
    if (selectedRows.length === 0) return;
    setBulkPrinting(true);
    try {
      const orderIds = selectedRows.map((o: any) => o.id);
      const chosenSize = size || labelFormat || '4x3';
      const res = await adminAPI.generateLabels(orderIds, undefined, chosenSize);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWin = window.open(url, '_blank');
      if (printWin) {
        printWin.addEventListener('load', () => {
          setTimeout(() => { try { printWin.print(); } catch {} }, 600);
        });
      }
    } catch (error: any) {
      console.error('Failed to bulk print labels:', error);
      alert(`Failed to generate labels: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
    } finally {
      setBulkPrinting(false);
    }
  };

  // ── Bulk Category Assignment ──
  // Category → status mapping (same as bulk)
  const CATEGORY_STATUS_MAP: Record<string, string> = {
    new:                 'pending',
    incomplete:          'preorder',
    future:              'preorder',
    not_response:        'no_response',
    confirmed:           'processing',
    confirmed_sent:      'processing',
    in_querier:          'shipped',
    partially_delivered: 'partially_delivered',
    delivered:           'delivered',
    cancelled:           'cancelled',
  };

  // Status → category mapping (reverse) — used as fallback when no steadfast_consignment_id
  const STATUS_CATEGORY_MAP: Record<string, string> = {
    preorder:            'incomplete',
    pending:             'new',
    no_response:         'not_response',
    processing:          'confirmed',
    shipped:             'in_querier',
    in_querier:          'in_querier',
    partially_delivered: 'partially_delivered',
    partial_delivered:   'partially_delivered',
    delivered:           'delivered',
    cancelled:           'cancelled',
  };

  // Smart category resolver — considers courier status, order status and consignment
  const getOrderCategory = (order: any): string => {
    const st = order.status?.toLowerCase();
    const sfSt = order.steadfast_status?.toLowerCase();

    // Explicit order & courier status matching
    if (st === 'delivered' || sfSt === 'delivered') return 'delivered';
    if (st === 'partially_delivered' || sfSt === 'partial_delivered') return 'partially_delivered';
    if (st === 'cancelled' || sfSt === 'cancelled') return 'cancelled';
    if (st === 'shipped' || st === 'in_querier' || sfSt === 'in_transit') return 'in_querier';
    if (st === 'no_response') return 'not_response';
    if (st === 'pending') return 'new';
    if (st === 'preorder' || order.is_preorder) return 'incomplete';
    if (st === 'processing') {
      return order.steadfast_consignment_id ? 'confirmed_sent' : 'confirmed';
    }
    if (order.steadfast_consignment_id) return 'in_querier';
    return STATUS_CATEGORY_MAP[st] || '';
  };

  const CATEGORY_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
    new:                 { label: '🆕 New',                 color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd' },
    incomplete:          { label: '📋 Incomplete',          color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    future:              { label: '📋 Incomplete',          color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    not_response:        { label: '📞 Not Response',        color: '#b45309', bg: '#fef3c7', border: '#fcd34d' },
    confirmed:           { label: '✅ Confirmed',            color: '#065f46', bg: '#d1fae5', border: '#6ee7b7' },
    confirmed_sent:      { label: '🚚 Confirmed & Sent',    color: '#0f4c75', bg: '#e0f2fe', border: '#7dd3fc' },
    in_querier:          { label: '🚚 In Querier',          color: '#0369a1', bg: '#e0f2fe', border: '#38bdf8' },
    partially_delivered: { label: '📦 Partially Delivered', color: '#c2410c', bg: '#ffedd5', border: '#fdba74' },
    delivered:           { label: '🎉 Delivered',            color: '#15803d', bg: '#dcfce7', border: '#86efac' },
    cancelled:           { label: '❌ Cancelled',            color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' },
  };

  const handleCategoryChange = async (orderId: number, category: string) => {
    const targetStatus = CATEGORY_STATUS_MAP[category];
    if (!targetStatus) return;
    setCategoryUpdating(prev => new Set(prev).add(orderId));
    try {
      const res = await adminAPI.updateOrderStatus(orderId, targetStatus);
      const updatedData = res?.data || res || {};
      setOrders(prev => prev.map(o => {
        if (o.id !== orderId) return o;
        return {
          ...o,
          ...updatedData,
          status: targetStatus,
          is_preorder: targetStatus === 'preorder',
        };
      }));
    } catch (err: any) {
      console.error('Failed to update category:', err);
      const errorMsg = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Failed to update order status';
      alert(`Error updating order status: ${errorMsg}`);
    } finally {
      setCategoryUpdating(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleBulkCategoryAssign = async () => {
    if (!assignCategory || selectedRows.length === 0) return;
    const statusMap: Record<string, string> = {
      new:                 'pending',
      incomplete:          'preorder',
      future:              'preorder',
      not_response:        'no_response',
      confirmed:           'processing',
      confirmed_sent:      'processing',
      in_querier:          'shipped',
      partially_delivered: 'partially_delivered',
      delivered:           'delivered',
      cancelled:           'cancelled',
    };
    const targetStatus = statusMap[assignCategory];
    if (!targetStatus) return;
    const categoryLabelMap: Record<string, string> = {
      new:                 'New',
      incomplete:          'Incomplete',
      future:              'Incomplete',
      not_response:        'Not Response',
      confirmed:           'Confirmed',
      confirmed_sent:      'Confirmed & Sent',
      in_querier:          'In Querier',
      partially_delivered: 'Partially Delivered',
      delivered:           'Delivered',
      cancelled:           'Cancelled',
    };
    const categoryLabel = categoryLabelMap[assignCategory] ?? assignCategory.charAt(0).toUpperCase() + assignCategory.slice(1);
    if (!window.confirm(`Move ${selectedRows.length} order(s) to "${categoryLabel}"?`)) return;
    setBulkAssigning(true);
    try {
      await Promise.all(selectedRows.map(order => adminAPI.updateOrderStatus(order.id, targetStatus)));
      await loadOrders();
      setAssignCategory('');
    } catch (error: any) {
      console.error('Failed to assign category:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to assign category'}`);
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select orders to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete ${selectedRows.length} selected order(s)?\n\nThis action cannot be undone and will automatically restore product inventory for active orders.`)) {
      return;
    }

    try {
      await adminAPI.bulkDeleteOrders(selectedRows);
      if (selectedOrder && selectedRows.includes(selectedOrder.id)) {
        closeViewModal();
      }
      setSelectedRows([]);
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to bulk delete orders:', error);
      alert(`Error: ${error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete orders'}`);
    }
  };

  // ── Sync Steadfast Statuses (bulk) ──
  const handleSyncSteadfastStatuses = async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await adminAPI.syncSteadfastStatuses();
      const data = res.data;
      const changed = (data.results || []).filter((r: any) => r.changed).length;
      setLastSyncTime(new Date().toLocaleTimeString());
      setSyncStats({ synced: data.synced || 0, changed });
      // Always reload orders to reflect current state
      await loadOrders();
    } catch (err: any) {
      if (!silent) {
        console.error('Sync failed:', err);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleViewTracking = async (orderId: number, consignmentId: string) => {
    try {
      const response = await adminAPI.getSteadfastTracking(orderId);
      const data = response.data;
      
      if (data.success) {
        // Refresh this order in the grid
        const response2 = await adminAPI.getOrders();
        const d = response2.data || response2;
        const ordersList = Array.isArray(d) ? d : (d.results || []);
        setOrders(ordersList);

        const trackingInfo = data.tracking_data;
        const sfStatus = data.steadfast_status || (typeof trackingInfo === 'string' ? trackingInfo : trackingInfo?.delivery_status) || 'Unknown';
        alert(`Steadfast Status: ${sfStatus.replace(/_/g, ' ')}\n\nConsignment: ${data.consignment_id}\nTracking: ${data.tracking_code}`);
      } else {
        alert(`Failed to get tracking info: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Failed to get tracking info:', error);
      alert(`Error: ${error.response?.data?.error || error.message || 'Failed to get tracking info'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Ensure orders is always an array before filtering
  const ordersArray = Array.isArray(orders) ? orders : [];

  // Phone numbers that already have an active placed order (not preorder, not cancelled)
  const activePlacedPhones = useMemo(() => {
    const phones = new Set<string>();
    ordersArray.forEach(o => {
      const st = (o.status || '').toLowerCase();
      if (st !== 'preorder' && st !== 'cancelled' && !o.is_preorder) {
        const p = (o.customer_phone || '').replace(/[\s\-\(\)\+]/g, '');
        if (p.length >= 8) {
          phones.add(p.slice(-8));
        }
      }
    });
    return phones;
  }, [ordersArray]);

  // Count per group for badges
  const groupCounts: Record<string, number> = { all: ordersArray.length };
  Object.entries(STATUS_GROUPS).forEach(([group, statuses]) => {
    if (group === 'confirmed') {
      // confirmed = processing without courier consignment
      groupCounts[group] = ordersArray.filter(o =>
        statuses.includes(o.status?.toLowerCase()) && !o.steadfast_consignment_id
      ).length;
    } else if (group === 'incomplete' || group === 'future') {
      // Incomplete orders excluding any customer who has already placed a real order
      groupCounts[group] = ordersArray.filter(o => {
        const isPre = statuses.includes(o.status?.toLowerCase()) || o.is_preorder;
        if (!isPre) return false;
        const p = (o.customer_phone || '').replace(/[\s\-\(\)\+]/g, '');
        if (p.length >= 8 && activePlacedPhones.has(p.slice(-8))) {
          return false; // Order was placed! Hide from incomplete
        }
        return true;
      }).length;
    } else {
      groupCounts[group] = ordersArray.filter(o => statuses.includes(o.status?.toLowerCase())).length;
    }
  });
  // confirmed_sent = any order that has been sent to courier
  groupCounts['confirmed_sent'] = ordersArray.filter(o => !!o.steadfast_consignment_id).length;
  groupCounts['in_querier'] = ordersArray.filter(o =>
    o.status?.toLowerCase() === 'shipped' ||
    o.status?.toLowerCase() === 'in_querier' ||
    (!!o.steadfast_consignment_id && !['delivered', 'partially_delivered', 'cancelled'].includes(o.status?.toLowerCase()))
  ).length;

  const matchesOrderStatus = (order: any, filter: StatusFilter): boolean => {
    if (filter === 'confirmed_sent') {
      return !!order.steadfast_consignment_id;
    } else if (filter === 'in_querier') {
      return order.status?.toLowerCase() === 'shipped' ||
        order.status?.toLowerCase() === 'in_querier' ||
        (!!order.steadfast_consignment_id && !['delivered', 'partially_delivered', 'cancelled'].includes(order.status?.toLowerCase()));
    } else if (filter === 'confirmed') {
      return ['processing'].includes(order.status?.toLowerCase()) &&
        !order.steadfast_consignment_id;
    } else if (filter === 'incomplete' || filter === 'future') {
      const isPre = order.status?.toLowerCase() === 'preorder' || order.is_preorder;
      const p = (order.customer_phone || '').replace(/[\s\-\(\)\+]/g, '');
      if (!isPre || (p.length >= 8 && activePlacedPhones.has(p.slice(-8)))) {
        return false;
      }
      return true;
    } else if (filter !== 'all') {
      const group = STATUS_GROUPS[filter];
      if (group) {
        return group.includes(order.status?.toLowerCase());
      } else {
        return order.status?.toLowerCase() === filter;
      }
    }
    return true;
  };

  const matchesOrderDate = (order: any, filter: StatusFilter, basis: 'auto' | 'created_at' | 'status_date', sDate: string, eDate: string): boolean => {
    if (!sDate && !eDate) return true;
    const orderDate = getOrderFilterDate(order, filter, basis);
    if (!orderDate) return false;
    if (sDate) {
      const start = new Date(sDate);
      start.setHours(0, 0, 0, 0);
      if (orderDate < start) return false;
    }
    if (eDate) {
      const end = new Date(eDate);
      end.setHours(23, 59, 59, 999);
      if (orderDate > end) return false;
    }
    return true;
  };

  const matchesTrafficSource = (order: any, filter: string): boolean => {
    if (!order) return false;
    if (!filter || filter === 'all') return true;
    const source = (order.traffic_source || (order.source === 'manual' ? 'manual' : 'organic')).toLowerCase();
    if (filter === 'facebook') return source === 'facebook';
    if (filter === 'tiktok') return source === 'tiktok';
    if (filter === 'google') return source === 'google';
    if (filter === 'whatsapp') return source === 'whatsapp';
    if (filter === 'manual') return source === 'manual';
    if (filter === 'organic') return source === 'organic';
    return true;
  };

  const renderTrafficBadge = (order: any) => <TrafficSourceBadge order={order} />;

  // Total matching orders across all statuses for the active search query
  const totalSearchMatches = useMemo(() => {
    const term = searchTerm.trim();
    if (!term) return 0;
    return ordersArray.filter(o => matchesOrderSearch(o, term)).length;
  }, [ordersArray, searchTerm]);

  // Orders matching active status tab + date + search query + traffic source
  const tabFilteredOrders = useMemo(() => {
    return ordersArray.filter(order => {
      const matchesSearch = matchesOrderSearch(order, searchTerm);
      const matchesStatus = matchesOrderStatus(order, statusFilter);
      const matchesDate = matchesOrderDate(order, statusFilter, dateFilterBasis, startDate, endDate);
      const matchesSource = matchesTrafficSource(order, trafficSourceFilter);
      return matchesSearch && matchesStatus && matchesDate && matchesSource;
    });
  }, [ordersArray, searchTerm, statusFilter, dateFilterBasis, startDate, endDate, trafficSourceFilter]);

  // Smart fallback: if user is searching and 0 matches exist in the selected tab,
  // but matches exist in other categories, show the matching orders automatically!
  const isSearchFallback = Boolean(
    searchTerm.trim() &&
    statusFilter !== 'all' &&
    tabFilteredOrders.length === 0 &&
    totalSearchMatches > 0
  );

  const filteredOrders = useMemo(() => {
    if (isSearchFallback) {
      return ordersArray.filter(order => {
        const matchesSearch = matchesOrderSearch(order, searchTerm);
        const matchesDate = matchesOrderDate(order, 'all', dateFilterBasis, startDate, endDate);
        const matchesSource = matchesTrafficSource(order, trafficSourceFilter);
        return matchesSearch && matchesDate && matchesSource;
      });
    }
    return tabFilteredOrders;
  }, [isSearchFallback, tabFilteredOrders, ordersArray, searchTerm, dateFilterBasis, startDate, endDate, trafficSourceFilter]);

  const ordersToExport = selectedRows.length > 0 ? selectedRows : filteredOrders;

  const handleExportMohammadParcel = () => {
    const suffix = selectedRows.length > 0 ? `selected_${selectedRows.length}` : (statusFilter !== 'all' ? statusFilter : '');
    exportMohammadParcelXLSX(ordersToExport, suffix);
    setExportDropdownOpen(false);
  };

  const handleExportProductDealer = () => {
    const suffix = selectedRows.length > 0 ? `selected_${selectedRows.length}` : (statusFilter !== 'all' ? statusFilter : '');
    exportProductDealerXLSX(ordersToExport, suffix);
    setExportDropdownOpen(false);
  };

  const handleExportStandardXLSX = () => {
    const filterKey = selectedRows.length > 0 ? `selected_${selectedRows.length}` : statusFilter;
    exportStandardXLSX(ordersToExport, filterKey, (order) => CATEGORY_LABELS[getOrderCategory(order) as keyof typeof CATEGORY_LABELS]?.label || '');
    setExportDropdownOpen(false);
  };

  const handleExportStandardCSV = () => {
    const filterKey = selectedRows.length > 0 ? `selected_${selectedRows.length}` : statusFilter;
    exportStandardCSV(ordersToExport, filterKey, (order) => CATEGORY_LABELS[getOrderCategory(order) as keyof typeof CATEGORY_LABELS]?.label || '');
    setExportDropdownOpen(false);
  };

  // ── Sort + Paginate ──


  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a: any, b: any) => {
      let va: any, vb: any;
      if (sortField === 'product_title') {
        va = (a.items?.[0]?.product_name || '').toLowerCase();
        vb = (b.items?.[0]?.product_name || '').toLowerCase();
      } else if (sortField === 'created_at') {
        const da = getOrderFilterDate(a, statusFilter, dateFilterBasis);
        const db = getOrderFilterDate(b, statusFilter, dateFilterBasis);
        va = da ? da.getTime() : 0;
        vb = db ? db.getTime() : 0;
      } else {
        va = (a[sortField] ?? '');
        vb = (b[sortField] ?? '');
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredOrders, sortField, sortDir, statusFilter, dateFilterBasis]);

  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, sortedOrders.length);
  const pagedOrders = sortedOrders.slice(startIndex, endIndex);

  // Batch load courier intelligence for phones on the active page
  useEffect(() => {
    const phonesToFetch: string[] = [];
    pagedOrders.forEach((o: any) => {
      const normalized = normalizeCustomerPhone(o.customer_phone);
      if (/^01[3-9]\d{8}$/.test(normalized)) {
        if (!customerRiskMap[normalized] && !riskLoadingPhones.has(normalized)) {
          if (!phonesToFetch.includes(normalized)) {
            phonesToFetch.push(normalized);
          }
        }
      }
    });

    if (phonesToFetch.length === 0) return;

    setRiskLoadingPhones(prev => {
      const next = new Set(prev);
      phonesToFetch.forEach(p => next.add(p));
      return next;
    });

    adminAPI.batchCustomerCourierIntelligence(phonesToFetch)
      .then((res: any) => {
        const results = res.data?.results || {};
        setCustomerRiskMap(prev => ({ ...prev, ...results }));
      })
      .catch((err: any) => {
        console.error('Failed to load batch courier intelligence', err);
      })
      .finally(() => {
        setRiskLoadingPhones(prev => {
          const next = new Set(prev);
          phonesToFetch.forEach(p => next.delete(p));
          return next;
        });
      });
  }, [pagedOrders]);

  const selectedIds = new Set(selectedRows.map((o: any) => o.id));

  const handleSelectRow = (order: any, checked: boolean) => {
    if (checked) setSelectedRows((prev: any[]) => [...prev, order]);
    else setSelectedRows((prev: any[]) => prev.filter((o: any) => o.id !== order.id));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedRows(pagedOrders);
    else setSelectedRows([]);
  };

  return (
    <div className="admin-page om-page">

      {/* ── Top Action Bar ── */}
      <div className="om-topbar">
        <h1 className="om-page-title">Order Management</h1>
        <div className="om-topbar-right">
          {/* Export Dropdown */}
          <div className="om-export-dropdown-wrap" ref={exportDropdownRef}>
            <button
              type="button"
              className="om-btn om-btn-csv om-export-btn"
              onClick={() => setExportDropdownOpen(prev => !prev)}
              title="Export orders to Excel (.xlsx) or CSV"
            >
              <Download size={13} />
              <span>EXPORT</span>
              <span className="om-btn-badge">{ordersToExport.length}</span>
              <ChevronDown size={12} className={`om-export-chevron ${exportDropdownOpen ? 'open' : ''}`} />
            </button>

            {exportDropdownOpen && (
              <div className="om-export-menu">
                <div className="om-export-menu-header">
                  <span>Export {ordersToExport.length} {selectedRows.length > 0 ? 'Selected' : 'Filtered'} Orders</span>
                </div>
                
                <button
                  type="button"
                  className="om-export-item om-export-item-highlight"
                  onClick={handleExportMohammadParcel}
                >
                  <div className="om-export-item-icon">📦</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Mohammad Parcel <span className="om-format-tag xlsx">.XLSX</span>
                    </div>
                    <div className="om-export-item-desc">
                      Invoice Number, Customer Name, Phone, Address, Cod Amount, Note
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="om-export-item om-export-item-highlight"
                  onClick={handleExportProductDealer}
                >
                  <div className="om-export-item-icon">🏷️</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Product Dealer Sheet <span className="om-format-tag xlsx">.XLSX</span>
                    </div>
                    <div className="om-export-item-desc">
                      Invoice Number, Customer Name, Customer Phone, Note
                    </div>
                  </div>
                </button>

                <div className="om-export-divider" />

                <button
                  type="button"
                  className="om-export-item"
                  onClick={handleExportStandardXLSX}
                >
                  <div className="om-export-item-icon">📊</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Standard Orders Report <span className="om-format-tag xlsx">.XLSX</span>
                    </div>
                    <div className="om-export-item-desc">
                      Full order details, variants, courier status & timestamps
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="om-export-item"
                  onClick={handleExportStandardCSV}
                >
                  <div className="om-export-item-icon">📄</div>
                  <div className="om-export-item-content">
                    <div className="om-export-item-title">
                      Standard Orders Export <span className="om-format-tag csv">.CSV</span>
                    </div>
                    <div className="om-export-item-desc">
                      Traditional comma-separated values file
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
          <button
            className="om-btn om-btn-manual"
            onClick={handleOpenManualModal}
          >
            <Phone size={13} /> New Order
          </button>
          <button
            className="om-btn om-btn-sync"
            onClick={() => handleSyncSteadfastStatuses(false)}
            disabled={syncing}
            title={lastSyncTime ? `Last synced: ${lastSyncTime}` : 'Sync Steadfast delivery statuses'}
          >
            {syncing
              ? <><span className="om-spin"><Loader2 size={13} /></span> Syncing</>
              : <><RefreshCw size={13} /> Sync</>
            }
            {syncStats && !syncing && (
              <span className="om-btn-badge">{syncStats.changed > 0 ? `${syncStats.changed}↑` : <CheckCircle2 size={10} />}</span>
            )}
          </button>

          {/* Bulk: Send to Steadfast */}
          {selectedRows.filter((o: any) => !o.steadfast_consignment_id).length > 0 && (
            <button className="om-btn om-btn-sf" onClick={handleBulkSendToSteadfast} disabled={bulkSending}>
              {bulkSending ? <span className="om-spin"><Loader2 size={13} /></span> : <Package size={13} />} Send to SF
              <span className="om-btn-badge">{selectedRows.filter((o: any) => !o.steadfast_consignment_id).length}</span>
            </button>
          )}
          {/* Bulk: Pickup */}
          {selectedRows.filter((o: any) => !!o.steadfast_consignment_id).length > 0 && (
            <button className="om-btn om-btn-pickup" onClick={handleBulkPickupRequest} disabled={pickupRequesting}>
              {pickupRequesting ? <span className="om-spin"><Loader2 size={13} /></span> : <Truck size={13} />} Pickup
              <span className="om-btn-badge">{selectedRows.filter((o: any) => !!o.steadfast_consignment_id).length}</span>
            </button>
          )}
          {/* Bulk: Print Labels */}
          {selectedRows.length > 0 && (
            <div className="om-print-wrapper" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button
                className="om-btn om-btn-print"
                onClick={() => handleBulkPrintLabels(labelFormat)}
                disabled={bulkPrinting}
                title={`Print ${selectedRows.length} dispatch label(s)`}
              >
                {bulkPrinting ? <span className="om-spin"><Loader2 size={13} /></span> : <Printer size={13} />} Print
                <span className="om-btn-badge">{selectedRows.length}</span>
              </button>
              <select
                value={labelFormat}
                onChange={(e) => {
                  setLabelFormat(e.target.value);
                  localStorage.setItem('order_label_format', e.target.value);
                }}
                className="om-print-format-select"
                title="Select Thermal Sticker Size"
              >
                <option value="4x3">Courier Sticker (4×3")</option>
                <option value="4x2">4×2" Landscape</option>
                <option value="2x4">2×4" Portrait</option>
              </select>
            </div>
          )}
          {/* Bulk: Assign Category */}
          {selectedRows.length > 0 && (
            <>
              <select value={assignCategory} onChange={(e) => setAssignCategory(e.target.value)} className="om-cat-select">
                <option value="">Move to…</option>
                <option value="new">New</option>
                <option value="incomplete">Incomplete</option>
                <option value="not_response">Not Response</option>
                <option value="confirmed">Confirmed</option>
                <option value="confirmed_sent">Conf. &amp; Sent</option>
                <option value="in_querier">In Querier</option>
                <option value="partially_delivered">Partially Delivered</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {assignCategory && (
                <button className="om-btn om-btn-save" onClick={handleBulkCategoryAssign} disabled={bulkAssigning}>
                  {bulkAssigning ? <span className="om-spin"><Loader2 size={13} /></span> : <Save size={13} />} Save
                  <span className="om-btn-badge">{selectedRows.length}</span>
                </button>
              )}
              <button className="om-btn om-btn-del" onClick={handleBulkDelete}>
                <Trash2 size={13} /> {selectedRows.length}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Filter Chips ── */}
      <div className="om-filter-wrap" ref={filterWrapRef}>
        <div className="om-filter-status">
          <select 
            className="om-status-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setCurrentPage(1); }}
          >
            {([
              { key: 'all',                 label: 'All',                 count: ordersArray.length },
              { key: 'new',                 label: 'New Orders',          count: groupCounts.new ?? 0 },
              { key: 'confirmed',           label: 'Confirmed',           count: groupCounts.confirmed ?? 0 },
              { key: 'in_querier',          label: 'In Querier',          count: groupCounts.in_querier ?? 0 },
              { key: 'confirmed_sent',      label: 'Ready To Delivery',   count: groupCounts.confirmed_sent ?? 0 },
              { key: 'partially_delivered', label: 'Partially Delivered', count: groupCounts.partially_delivered ?? 0 },
              { key: 'delivered',           label: 'Delivered',           count: groupCounts.delivered ?? 0 },
              { key: 'not_response',        label: 'Not Response',        count: groupCounts.not_response ?? 0 },
              { key: 'cancelled',           label: 'Cancelled',           count: groupCounts.cancelled ?? 0 },
              { key: 'incomplete',          label: 'Incomplete',          count: (groupCounts.incomplete ?? groupCounts.future ?? 0) },
            ] as { key: string; label: string; count: number }[]).map(chip => (
              <option key={chip.key} value={chip.key}>
                {chip.label} : {chip.count}
              </option>
            ))}
          </select>
        </div>

        <div className="om-filter-row2">
          <div className="om-date-range">
            <div className="om-date-basis-wrap">
              <select
                value={dateFilterBasis}
                onChange={e => { setDateFilterBasis(e.target.value as any); setCurrentPage(1); }}
                className="om-date-basis-select"
                title="Filter date basis"
              >
                <option value="auto">
                  {statusFilter === 'confirmed' ? '⚡ Confirmed Date' :
                   statusFilter === 'in_querier' || statusFilter === 'confirmed_sent' ? '🚚 Sent to Courier Date' :
                   statusFilter === 'delivered' ? '🎉 Delivered Date' :
                   statusFilter === 'cancelled' ? '❌ Cancelled Date' :
                   '📅 Stage Date (Auto)'}
                </option>
                <option value="created_at">📅 Placed Date</option>
                <option value="status_date">🕒 Status Date</option>
              </select>
            </div>
            <div className="om-date-inputs-row">
              <input
                type="date" value={startDate}
                onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }}
                className="om-date-input" title="From date"
              />
              <span className="om-date-sep">to</span>
              <input
                type="date" value={endDate}
                onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }}
                className="om-date-input" title="To date"
              />
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={() => { setStartDate(''); setEndDate(''); setCurrentPage(1); }}
                  className="om-date-clear"
                  title="Clear dates"
                >
                  <X size={11} />
                </button>
              )}
            </div>
          </div>

          {/* Traffic Source Filter */}
          <div className="om-traffic-source-wrap">
            <select
              value={trafficSourceFilter}
              onChange={e => { setTrafficSourceFilter(e.target.value); setCurrentPage(1); }}
              className="om-traffic-source-select"
              title="Filter by Traffic Source / Ad Platform"
            >
              <option value="all">🎯 All Sources ({ordersArray.length})</option>
              <option value="facebook">🟦 Facebook ({ordersArray.filter(o => matchesTrafficSource(o, 'facebook')).length})</option>
              <option value="tiktok">🎵 TikTok ({ordersArray.filter(o => matchesTrafficSource(o, 'tiktok')).length})</option>
              <option value="whatsapp">💬 WhatsApp ({ordersArray.filter(o => matchesTrafficSource(o, 'whatsapp')).length})</option>
              <option value="google">🔍 Google Ads ({ordersArray.filter(o => matchesTrafficSource(o, 'google')).length})</option>
              <option value="organic">🌐 Direct / Website ({ordersArray.filter(o => matchesTrafficSource(o, 'organic')).length})</option>
              <option value="manual">📞 Manual ({ordersArray.filter(o => matchesTrafficSource(o, 'manual')).length})</option>
            </select>
          </div>

          <div className="om-search-wrap">
            <span className="om-search-icon"><Search size={13} /></span>
            <input
              type="text"
              placeholder={isMobile ? "Search phone, ID, customer..." : "Search by mobile number, order ID, customer..."}
              value={searchTerm}
              onChange={handleSearch}
              className="om-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="om-search-clear"
                onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Search Feedback Banner ── */}
      {searchTerm.trim() && statusFilter !== 'all' && totalSearchMatches > 0 && (
        isSearchFallback ? (
          <div className="om-search-banner is-fallback">
            <span>
              ℹ️ No orders in <strong>{STATUS_FILTER_LABELS[statusFilter] || statusFilter}</strong> match &ldquo;<strong>{searchTerm}</strong>&rdquo;. Showing <strong>{totalSearchMatches}</strong> matching order{totalSearchMatches > 1 ? 's' : ''} found in other categories.
            </span>
            <button
              type="button"
              className="om-search-banner-btn"
              onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
            >
              Switch to All Categories
            </button>
          </div>
        ) : (
          totalSearchMatches > tabFilteredOrders.length ? (
            <div className="om-search-banner">
              <span>
                Showing <strong>{tabFilteredOrders.length}</strong> matching order{tabFilteredOrders.length > 1 ? 's' : ''} in <strong>{STATUS_FILTER_LABELS[statusFilter] || statusFilter}</strong>. (<strong>{totalSearchMatches - tabFilteredOrders.length}</strong> more found in other categories).
              </span>
              <button
                type="button"
                className="om-search-banner-btn"
                onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
              >
                View All Categories ({totalSearchMatches})
              </button>
            </div>
          ) : null
        )
      )}

      {/* ── MOBILE CARD LIST ── */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading orders…</div>}
          {!loading && filteredOrders.length === 0 && <div className="mc-empty">No orders found.</div>}
          {!loading && filteredOrders.length > 0 && (
            <div className="mc-select-all-bar">
              <label className="mc-select-all-label">
                <input
                  type="checkbox"
                  className="om-checkbox mc-card-checkbox"
                  checked={filteredOrders.length > 0 && filteredOrders.every(o => selectedIds.has(o.id))}
                  onChange={e => {
                    if (e.target.checked) setSelectedRows(filteredOrders);
                    else setSelectedRows([]);
                  }}
                />
                <span>Select All Orders ({filteredOrders.length})</span>
              </label>
              {selectedRows.length > 0 && (
                <span className="mc-selected-count-badge">
                  {selectedRows.length} selected
                </span>
              )}
            </div>
          )}
          {!loading && filteredOrders.map((order) => {
            const firstItem = order.items?.[0] || {};
            const imgUrl = getImageUrl(firstItem.product_image || firstItem.image);
            const productName = firstItem.product_name || firstItem.name || 'Unknown Product';
            const extraItems = (order.items?.length || 1) - 1;
            const statusKey = (order.status || 'pending').toLowerCase();
            const statusClass = statusKey.replace(/_/g, '-');
            const statusLabel = (order.status || 'Pending').replace(/_/g, ' ');
            const color  = firstItem.selected_color  || '';
            const size   = firstItem.selected_size   || '';
            const weight = firstItem.selected_weight || '';
            const isSending  = sendingToSteadfast === order.id;
            const isPrinting = printingLabel === order.id;
            const currentCategory = getOrderCategory(order);
            const categoryConfig  = CATEGORY_LABELS[currentCategory];
            const isSelected = selectedIds.has(order.id);
            const milestones = getOrderMilestones(order);
            return (
              <div className={`mobile-card${isSelected ? ' is-selected' : ''}`} key={order.id}>
                {/* 1. Header: Checkbox + Order # + Time + Status Badge */}
                <div className="mc-card-header">
                  <div className="mc-header-left">
                    <label className="mc-checkbox-label" onClick={(e) => e.stopPropagation()} title="Select order">
                      <input
                        type="checkbox"
                        className="om-checkbox mc-card-checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(order, e.target.checked)}
                      />
                    </label>
                    <div className="mc-order-ident">
                      <span className="mc-order-num">{renderHighlightedText(order.order_number, searchTerm)}</span>
                      <span className="mc-order-time">{format12HourDateTime(order.created_at)}</span>
                    </div>
                  </div>
                  <div className="mc-header-right">
                    <OrderStatusBadge status={order.status} variant="badge" />
                  </div>
                </div>

                {/* 2. Product & Financials Row */}
                <div className="mc-product-row">
                  {imgUrl ? (
                    <img
                      className="mc-thumb"
                      src={imgUrl}
                      alt="Product"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder-product.jpg'; }}
                    />
                  ) : (
                    <div className="mc-thumb-placeholder">📦</div>
                  )}
                  <div className="mc-product-details">
                    <div className="mc-product-title">{productName}{extraItems > 0 ? ` (+${extraItems} more)` : ''}</div>
                    
                    <div className="mc-product-tags">
                      {color && (
                        <span className="mc-tag mc-tag-color">
                          <span className="mc-color-dot" style={{ background: color.toLowerCase() }} />
                          {color}
                        </span>
                      )}
                      {size && <span className="mc-tag mc-tag-size">{size}</span>}
                      {weight && <span className="mc-tag mc-tag-weight">{weight}</span>}
                      {renderTrafficBadge(order)}
                    </div>

                    <div className="mc-price-row">
                      <span className="mc-price-label">Total:</span>
                      <span className="mc-price-amount">
                        {parseFloat(order.discount_amount || 0) > 0 ? (
                          <>
                            ৳{parseFloat(order.final_amount || order.total_amount || 0).toFixed(0)}
                            <span className="mc-price-original">৳{parseFloat(order.total_amount || 0).toFixed(0)}</span>
                          </>
                        ) : (
                          `৳${parseFloat(order.total_amount || 0).toFixed(0)}`
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 3. Customer & Shipping Info Box */}
                <div className="mc-customer-box">
                  <div className="mc-cust-top">
                    <div className="mc-cust-name">
                      <User size={12} className="mc-cust-icon" />
                      <span>{renderHighlightedText(order.customer_name || 'Anonymous Customer', searchTerm)}</span>
                    </div>
                    {order.customer_phone && (() => {
                      const cleanPhone = String(order.customer_phone).replace(/[^0-9+]/g, '');
                      const normalized = normalizeCustomerPhone(order.customer_phone);
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                          <a 
                            href={`tel:${cleanPhone}`} 
                            className="mc-cust-phone-btn"
                            title={`Call ${cleanPhone} directly`}
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cleanPhone) {
                                window.location.href = `tel:${cleanPhone}`;
                              }
                            }}
                          >
                            <Phone size={11} className="mc-phone-icon" />
                            <span className="mc-phone-num">{renderHighlightedPhone(order.customer_phone, searchTerm)}</span>
                          </a>
                          <CustomerRiskBadge
                            data={customerRiskMap[normalized]}
                            loading={riskLoadingPhones.has(normalized)}
                            onClick={() => handleViewOrder(order)}
                          />
                        </div>
                      );
                    })()}
                  </div>

                  {order.shipping_address && (
                    <div className="mc-cust-address">
                      <MapPin size={11} className="mc-addr-icon" />
                      <span>{order.shipping_address}</span>
                    </div>
                  )}

                  {milestones.hasMilestones && (
                    <div className="mc-stage-badges">
                      {milestones.confirmedTime && (
                        <div className="mc-stage-badge mc-badge-confirmed" title={`Confirmed: ${format12HourDateTime(milestones.confirmedTime, true)}`}>
                          <span className="mc-badge-icon">⚡</span>
                          <span>Conf: {format12HourDateTime(milestones.confirmedTime)}</span>
                        </div>
                      )}
                      {milestones.courierTime && (
                        <div className="mc-stage-badge mc-badge-shipped" title={`Sent to Courier / In Querier: ${format12HourDateTime(milestones.courierTime, true)}`}>
                          <span className="mc-badge-icon">🚚</span>
                          <span>Courier: {format12HourDateTime(milestones.courierTime)}</span>
                        </div>
                      )}
                      {milestones.deliveredTime && (
                        <div className="mc-stage-badge mc-badge-delivered" title={`Delivered: ${format12HourDateTime(milestones.deliveredTime, true)}`}>
                          <span className="mc-badge-icon">🎉</span>
                          <span>Deliv: {format12HourDateTime(milestones.deliveredTime)}</span>
                        </div>
                      )}
                      {milestones.cancelledTime && (
                        <div className="mc-stage-badge mc-badge-cancelled" title={`Cancelled: ${format12HourDateTime(milestones.cancelledTime, true)}`}>
                          <span className="mc-badge-icon">❌</span>
                          <span>Canc: {format12HourDateTime(milestones.cancelledTime)}</span>
                        </div>
                      )}
                      {milestones.partialTime && (
                        <div className="mc-stage-badge mc-badge-partial" title={`Partially Delivered: ${format12HourDateTime(milestones.partialTime, true)}`}>
                          <span className="mc-badge-icon">📦</span>
                          <span>Part. Deliv: {format12HourDateTime(milestones.partialTime)}</span>
                        </div>
                      )}
                      {milestones.noResponseTime && (
                        <div className="mc-stage-badge mc-badge-no-response" title={`Not Response: ${format12HourDateTime(milestones.noResponseTime, true)}`}>
                          <span className="mc-badge-icon">📞</span>
                          <span>No Resp: {format12HourDateTime(milestones.noResponseTime)}</span>
                        </div>
                      )}
                      {milestones.incompleteTime && (
                        <div className="mc-stage-badge mc-badge-incomplete" title={`Incomplete: ${format12HourDateTime(milestones.incompleteTime, true)}`}>
                          <span className="mc-badge-icon">📋</span>
                          <span>Incomplete: {format12HourDateTime(milestones.incompleteTime)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 4. Workflow Stage Selector (Compact Bar) */}
                <div className="mc-stage-bar">
                  <span className="mc-stage-label">Stage:</span>
                  <select
                    className="mc-stage-select"
                    value={currentCategory}
                    onChange={(e) => handleCategoryChange(order.id, e.target.value)}
                    disabled={categoryUpdating.has(order.id)}
                    style={{
                      borderColor: categoryConfig?.border || '#cbd5e1',
                      background: categoryConfig?.bg || '#f8fafc',
                      color: categoryConfig?.color || '#334155',
                    }}
                  >
                    <option value="">— Select Stage —</option>
                    <option value="new">New</option>
                    <option value="incomplete">Incomplete</option>
                    <option value="not_response">Not Response</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="confirmed_sent">Conf. &amp; Sent</option>
                    <option value="in_querier">In Querier</option>
                    <option value="partially_delivered">Partially Delivered</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* 5. Harmonious Modern Action Toolbar */}
                <div className="mc-actions">
                  <button className="mc-btn mc-btn-view" onClick={() => handleViewOrder(order)} title="View order details">
                    <Eye size={12} />
                    <span>View</span>
                  </button>
                  <button className="mc-btn mc-btn-edit" onClick={() => handleEditOrder(order)} title="Edit order">
                    <Pencil size={12} />
                    <span>Edit</span>
                  </button>
                  <button className="mc-btn mc-btn-print" onClick={() => handlePrintSingleLabel(order.id)} disabled={isPrinting} title="Print parcel invoice">
                    <Printer size={12} />
                    <span>{isPrinting ? 'Printing…' : 'Print'}</span>
                  </button>
                  <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteOrder(order.id)} title="Delete order">
                    <Trash2 size={12} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP TABLE ── */}
      {!isMobile && (
        <div className="om-table-card">
          <div className="om-table-scroll">
            <table className="om-table">
              <colgroup>
                <col className="col-check" />
                <col className="col-product" />
                <col className="col-order" />
                <col className="col-customer" />
                <col className="col-address" />
                <col className="col-category" />
                <col className="col-variant" />
                <col className="col-finance" />
                <col className="col-status" />
                <col className="col-sf" />
                <col className="col-notes" />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="om-th om-th-check">
                    <input
                      type="checkbox"
                      className="om-checkbox"
                      checked={pagedOrders.length > 0 && selectedIds.size === pagedOrders.length}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  {([
                    { key: 'product',          label: 'Product',    sortKey: 'product_title' },
                    { key: 'order_number',     label: 'Order #',    sortKey: 'order_number' },
                    { key: 'customer',         label: 'Customer',   sortKey: 'customer_name' },
                    { key: 'shipping_address', label: 'Address',    sortKey: 'shipping_address' },
                    { key: 'category',         label: 'Category',   sortKey: '' },
                    { key: 'variant',          label: 'Variant',    sortKey: '' },
                    { key: 'total_amount',     label: 'Financials', sortKey: 'total_amount' },
                    { key: 'status',           label: 'Status',     sortKey: 'status' },
                    { key: 'steadfast',        label: 'Steadfast',  sortKey: '' },
                    { key: 'notes',            label: 'Notes',      sortKey: '' },
                    { key: 'actions',          label: 'Actions',    sortKey: '' },
                  ] as { key: string; label: string; sortKey: string }[]).map(col => (
                    <th
                      key={col.key}
                      className={`om-th${col.sortKey ? ' om-th-sort' : ''}`}
                      onClick={() => {
                        if (!col.sortKey) return;
                        if (sortField === col.sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        else { setSortField(col.sortKey); setSortDir('asc'); }
                        setCurrentPage(1);
                      }}
                    >
                      {col.label}
                      {sortField === col.sortKey && col.sortKey
                        ? <span className="om-sort-arrow">{sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />}</span>
                        : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="om-loading">Loading orders…</td></tr>
                )}
                {!loading && pagedOrders.length === 0 && (
                  <tr><td colSpan={11} className="om-empty">No orders found.</td></tr>
                )}
                {!loading && pagedOrders.map(order => {
                  const firstItem = order.items?.[0] || {};
                  const imgUrl = getImageUrl(firstItem.product_image || firstItem.image);
                  const productName = firstItem.product_name || firstItem.name || 'Unknown Product';
                  const sku = firstItem.sku || '';
                  const extraItems = (order.items?.length || 1) - 1;
                  const color  = firstItem.selected_color  || '';
                  const size   = firstItem.selected_size   || '';
                  const weight = firstItem.selected_weight || '';
                  const total    = parseFloat(order.total_amount    || 0);
                  const discount = parseFloat(order.discount_amount || 0);
                  const final    = parseFloat(order.final_amount    || total);
                  const shipping = parseFloat(order.shipping_cost   || 0);
                  const isSelected  = selectedIds.has(order.id);
                  const isSending   = sendingToSteadfast === order.id;
                  const isPrinting  = printingLabel === order.id;
                  const currentCategory = getOrderCategory(order);
                  const catCfg = CATEGORY_LABELS[currentCategory];
                  const desktopMilestones = getOrderMilestones(order);

                  return (
                    <tr key={order.id} className={`om-row${isSelected ? ' om-row-selected' : ''}`}>

                      {/* ── Checkbox ── */}
                      <td className="om-td om-td-check">
                        <input
                          type="checkbox"
                          className="om-checkbox"
                          checked={isSelected}
                          onChange={e => handleSelectRow(order, e.target.checked)}
                        />
                      </td>

                      {/* ── Product ── */}
                      <td className="om-td om-td-product">
                        <div className="om-product-cell">
                          {imgUrl
                            ? <img
                                src={imgUrl}
                                alt=""
                                className="om-product-img"
                                onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder-product.jpg'; }}
                              />
                            : <div className="om-product-img-placeholder"><Box size={16} /></div>
                          }
                          <div className="om-product-info">
                            <span className="om-product-name">
                              {productName}{extraItems > 0 ? ` (+${extraItems})` : ''}
                            </span>
                            {sku && <span className="om-product-sku">SKU: {sku}</span>}
                            {renderTrafficBadge(order)}
                          </div>
                        </div>
                      </td>

                      {/* ── Order # ── */}
                      <td className="om-td">
                        <div className="om-order-num">{renderHighlightedText(order.order_number, searchTerm)}</div>
                        <div className="om-order-date" title="Order Placed Date">
                          📅 {format12HourDateTime(order.created_at, true)}
                        </div>
                        {desktopMilestones.confirmedTime && (
                          <div className="om-stage-badge om-stage-confirmed" title={`Confirmed: ${format12HourDateTime(desktopMilestones.confirmedTime, true)}`}>
                            ⚡ Conf: {format12HourDateTime(desktopMilestones.confirmedTime)}
                          </div>
                        )}
                        {desktopMilestones.courierTime && (
                          <div className="om-stage-badge om-stage-shipped" title={`Sent to Courier / In Querier: ${format12HourDateTime(desktopMilestones.courierTime, true)}`}>
                            🚚 Courier: {format12HourDateTime(desktopMilestones.courierTime)}
                          </div>
                        )}
                        {desktopMilestones.deliveredTime && (
                          <div className="om-stage-badge om-stage-delivered" title={`Delivered: ${format12HourDateTime(desktopMilestones.deliveredTime, true)}`}>
                            🎉 Deliv: {format12HourDateTime(desktopMilestones.deliveredTime)}
                          </div>
                        )}
                        {desktopMilestones.cancelledTime && (
                          <div className="om-stage-badge om-stage-cancelled" title={`Cancelled: ${format12HourDateTime(desktopMilestones.cancelledTime, true)}`}>
                            ❌ Canc: {format12HourDateTime(desktopMilestones.cancelledTime)}
                          </div>
                        )}
                        {desktopMilestones.partialTime && (
                          <div className="om-stage-badge om-stage-partial" title={`Partially Delivered: ${format12HourDateTime(desktopMilestones.partialTime, true)}`}>
                            📦 Part: {format12HourDateTime(desktopMilestones.partialTime)}
                          </div>
                        )}
                        {desktopMilestones.noResponseTime && (
                          <div className="om-stage-badge om-stage-no-response" title={`Not Response: ${format12HourDateTime(desktopMilestones.noResponseTime, true)}`}>
                            📞 No Resp: {format12HourDateTime(desktopMilestones.noResponseTime)}
                          </div>
                        )}
                        {desktopMilestones.incompleteTime && (
                          <div className="om-stage-badge om-stage-incomplete" title={`Incomplete: ${format12HourDateTime(desktopMilestones.incompleteTime, true)}`}>
                            📋 Incomp: {format12HourDateTime(desktopMilestones.incompleteTime)}
                          </div>
                        )}
                      </td>

                      {/* ── Customer ── */}
                      <td className="om-td">
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.82rem', marginBottom: '1px' }}>
                          {renderHighlightedText(order.customer_name, searchTerm)}
                        </div>
                        {order.customer_phone ? (() => {
                          const cleanPhone = String(order.customer_phone).replace(/[^0-9+]/g, '');
                          const normalized = normalizeCustomerPhone(order.customer_phone);
                          return (
                            <div>
                              <a
                                href={`tel:${cleanPhone}`}
                                className="om-phone-link"
                                title={`Call ${cleanPhone}`}
                                role="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (cleanPhone) {
                                    window.location.href = `tel:${cleanPhone}`;
                                  }
                                }}
                              >
                                <Phone size={10} style={{ display: 'inline', verticalAlign: 'middle' }} />
                                <span>{renderHighlightedPhone(order.customer_phone, searchTerm)}</span>
                              </a>
                              <div>
                                <CustomerRiskBadge
                                  data={customerRiskMap[normalized]}
                                  loading={riskLoadingPhones.has(normalized)}
                                  onClick={() => handleViewOrder(order)}
                                />
                              </div>
                            </div>
                          );
                        })() : (
                          <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>—</div>
                        )}
                      </td>

                      {/* ── Address ── */}
                      <td className="om-td om-td-address">
                        <div className="om-address-text" style={{ lineHeight: '1.3', fontSize: '0.75rem' }}>{order.shipping_address || '—'}</div>
                      </td>

                      {/* ── Category ── */}
                      <td className="om-td">
                        {categoryUpdating.has(order.id) ? (
                          <span className="om-saving">Saving…</span>
                        ) : (
                          <select
                            value={currentCategory}
                            onChange={e => handleCategoryChange(order.id, e.target.value)}
                            className="om-cat-dropdown"
                            style={{
                              background: catCfg?.bg || '#f9fafb',
                              color: catCfg?.color || '#374151',
                              borderColor: catCfg?.border || '#e5e7eb',
                            }}
                          >
                            <option value="">— Select —</option>
                            <option value="new">New</option>
                            <option value="incomplete">Incomplete</option>
                            <option value="not_response">Not Response</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="confirmed_sent">Conf. &amp; Sent</option>
                            <option value="in_querier">In Querier</option>
                            <option value="partially_delivered">Partially Delivered</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </td>

                      {/* ── Variant ── */}
                      <td className="om-td om-td-variant">
                        <div className="om-variant-stack">
                          {color && (
                            <span className="om-chip-var om-chip-color">
                              <span className="om-color-dot" style={{ background: color.toLowerCase() }} />
                              {color}
                            </span>
                          )}
                          {size   && <span className="om-chip-var om-chip-size">{size}</span>}
                          {weight && <span className="om-chip-var om-chip-weight">{weight}</span>}
                          {!color && !size && !weight && <span className="om-no-variant">—</span>}
                        </div>
                      </td>

                      {/* ── Financials ── */}
                      <td className="om-td om-td-finance">
                        {discount > 0 ? (
                          <>
                            <span className="om-price-final">৳{final.toFixed(2)}</span>
                            <span className="om-price-orig">৳{total.toFixed(2)}</span>
                            <span className="om-price-ship">Shipping: ৳{shipping.toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <span className="om-price-main">৳{total.toFixed(2)}</span>
                            <span className="om-price-ship">Shipping: ৳{shipping.toFixed(2)}</span>
                          </>
                        )}
                      </td>

                      {/* ── Status ── */}
                      <td className="om-td">
                        <OrderStatusBadge status={order.status} variant="chip" />
                      </td>

                      {/* ── Steadfast ── */}
                      <td className="om-td om-td-sf">
                        {order.steadfast_tracking_code ? (
                          <>
                            <span className="om-sf-label">Courier tracking&</span>
                            <span className="om-sf-consignment">
                              Consignment ID: {order.steadfast_consignment_id || '—'}
                            </span>
                          </>
                        ) : (
                          <span className="om-no-variant">—</span>
                        )}
                      </td>

                      {/* ── Notes ── */}
                      <td className="om-td om-td-notes">
                        <span className="om-notes-text">{order.notes || '—'}</span>
                      </td>

                      {/* ── Actions ── */}
                      <td className="om-td om-td-actions">
                        <div className="om-actions-row">
                          <button
                            className="om-action-btn om-action-print"
                            title="Print Label"
                            onClick={() => handlePrintSingleLabel(order.id)}
                            disabled={isPrinting}
                          >
                            {isPrinting ? <span className="om-spin"><Loader2 size={12} /></span> : <Printer size={12} />}
                          </button>
                          <button
                            className="om-action-btn om-action-edit"
                            title="Edit Order"
                            onClick={() => handleEditOrder(order)}
                          >
                            <Pencil size={12} />
                          </button>
                          {order.steadfast_consignment_id && (
                            <button
                              className="om-action-btn om-action-track"
                              title="Track"
                              onClick={() => handleViewTracking(order.id, order.steadfast_consignment_id)}
                            >
                              <MapPin size={12} />
                            </button>
                          )}
                          <button
                            className="om-action-btn om-action-view"
                            title="View Details"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye size={12} />
                          </button>
                          <button
                            className="om-action-btn om-action-del"
                            title="Delete Order"
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {/* ── Pagination (Shared for Mobile & Desktop) ── */}
      {sortedOrders.length > 0 && (
        <div className="om-pagination">
          <div className="om-page-size-selector">
            <span className="om-page-size-label">Rows per page:</span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="om-page-size-select"
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          
          <div className="om-page-info">
            {startIndex + 1}-{endIndex} of {sortedOrders.length} orders
          </div>

          <div className="om-page-controls">
            <button
              className="om-page-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safeCurrentPage === 1}
              title="Previous Page"
            >
              ‹
            </button>
            <div className="om-page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = safeCurrentPage;
                if (totalPages <= 5) pageNum = i + 1;
                else if (safeCurrentPage <= 3) pageNum = i + 1;
                else if (safeCurrentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = safeCurrentPage - 2 + i;
                
                return (
                  <button
                    key={pageNum}
                    className={`om-page-num ${pageNum === safeCurrentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="om-page-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage === totalPages}
              title="Next Page"
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* ─── Manual Order Modal ─── */}
      <ManualOrderModal
        isOpen={manualModalOpen}
        onClose={() => setManualModalOpen(false)}
        onOrderCreated={(newOrder) => setOrders(prev => [newOrder, ...prev])}
        deliveryOptions={deliveryOptions}
        siteSettings={siteSettings}
      />

      {/* ─── Edit Order Modal ─── */}
      <EditOrderModal
        isOpen={editModalOpen}
        order={editingOrder}
        onClose={() => { setEditModalOpen(false); setEditingOrder(null); }}
        onOrderUpdated={(updatedOrder) => {
          setOrders(prev => prev.map(o => o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o));
          setEditingOrder(updatedOrder);
        }}
        deliveryOptions={deliveryOptions}
        siteSettings={siteSettings}
      />

      {/* ─── Order View Modal ─── */}
      <ViewOrderModal
        isOpen={viewModalOpen}
        order={selectedOrder}
        onClose={closeViewModal}
        onDelete={handleDeleteOrder}
      />

      {/* ─── Bulk Send to Steadfast Result Modal ─── */}
      <BulkSendResultModal
        result={bulkSendResult}
        onClose={() => setBulkSendResult(null)}
      />

      {/* ─── Pickup Request Result Modal (Manifest) ─── */}
      <PickupResultModal
        result={pickupResult}
        onClose={() => setPickupResult(null)}
      />
    </div>
  );
};

// ── Steadfast status badge colors (used in edit modal tracking section) ──
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function steadfastStatusStyle(status: string | null | undefined): { bg: string; text: string; icon: string } {
  switch (status?.toLowerCase()) {
    case 'in_review':
      return { bg: '#fef3c7', text: '#92400e', icon: '⏳' };
    case 'pending':
      return { bg: '#fef9c3', text: '#854d0e', icon: '🕐' };
    case 'in_transit':
      return { bg: '#dbeafe', text: '#1e40af', icon: '🚚' };
    case 'delivered':
      return { bg: '#d1fae5', text: '#065f46', icon: '✅' };
    case 'partial_delivered':
      return { bg: '#d1fae5', text: '#065f46', icon: '📦' };
    case 'cancelled':
      return { bg: '#fee2e2', text: '#991b1b', icon: '❌' };
    case 'hold':
      return { bg: '#fce7f3', text: '#9d174d', icon: '⏸️' };
    case 'delivered_approval_pending':
    case 'partial_delivered_approval_pending':
    case 'cancelled_approval_pending':
      return { bg: '#ede9fe', text: '#5b21b6', icon: '⏳' };
    case 'unknown':
      return { bg: '#f3f4f6', text: '#6b7280', icon: '❓' };
    default:
      return { bg: '#f3f4f6', text: '#6b7280', icon: '📋' };
  }
}

export default OrderManagement;
